/**
 * collection-filter.js
 * Handles:
 *   1. Panel open/close (dropdown toggles)
 *   2. Checkbox filter → builds URL → fetches updated product grid (AJAX)
 *   3. Price range apply
 *   4. Sort links (preserve active filters)
 *   5. Close on outside click / Escape
 *   6. Browser back/forward
 *   7. Infinite scroll — loads next page on scroll, appends with fade in
 *   8. Re-initializes after every AJAX swap (event delegation on document)
 */

(function () {
  'use strict';

  /* ── State ───────────────────────────────────────────── */
  let isLoading    = false;
  let currentPage  = 1;
  let totalPages   = 1;
  let suppressNextIntersection = false;

  /* ── Helpers ─────────────────────────────────────────── */
  function getFilterBars() {
    return document.querySelectorAll('.FilterBar');
  }

  function getParentFilterBar(element) {
    return element.closest('.FilterBar');
  }
  
  function getCollectionUrl() {
    const bar = document.querySelector('.FilterBar');
    return bar ? bar.dataset.collectionUrl : window.location.pathname;
  }

  function getSectionId() {
    const section = document.querySelector('[data-section-id]');
    return section ? section.dataset.sectionId : '';
  }

  function getGrid() {
    return document.getElementById('ProductGrid');
  }

  function getTotalPages() {
    const nav = document.querySelector('.pagination[data-total-pages]');
    return nav ? parseInt(nav.dataset.totalPages) : 1;
  }

  /* ── Get selected filter count ─────────────────────── */

  function getSelectedFilterCount(filterBar) {
    if (!filterBar) return 0;

    const checkedCount = filterBar.querySelectorAll(
      `[data-filter-input]:checked`
    ).length;

    return checkedCount;
  }

  /* ── Update results button ─────────────────────────── */

  function updateResultsButton(filterBar) {
    const button = document.querySelector('[data-csb-results-button]');

    if (!button || !filterBar) return;

    const text = button.querySelector(
      '.csb__results-button-text'
    );

    const count = getSelectedFilterCount(filterBar);

    // Update text
    text.textContent = count
      ? `Show (${count}) results`
      : 'Show results';

    // Toggle active state
    button.classList.toggle('is-active', !!count);

    // Dispatch custom event
    document.dispatchEvent(
      new CustomEvent('csb:results-count-updated', {
        detail: {
          count,
          filterBar,
          button
        }
      })
    );
  }

  const PRODUCT_GROUP_PARAM = 'filter.p.m.custom.product_group';

  function isExclusiveFilterBar(filterBar) {
    return filterBar && filterBar.dataset.filterBarMode === 'exclusive';
  }

  function isProductGroupInput(input) {
    return input && input.name === PRODUCT_GROUP_PARAM;
  }

  /**
   * Top bar: one product group (merchandising tab).
   * Sidebar (e.g. Frame Name): multi-select when no top tab is active.
   */
  function appendProductGroupParams(params, filterBars) {
    let topValue = null;
    const sidebarValues = [];

    filterBars.forEach((filterBar) => {
      const checked = filterBar.querySelectorAll(
        `[data-filter-input][name="${PRODUCT_GROUP_PARAM}"]:checked`
      );

      if (isExclusiveFilterBar(filterBar)) {
        if (checked[0]) topValue = checked[0].value;
      } else {
        checked.forEach((input) => sidebarValues.push(input.value));
      }
    });

    if (topValue) {
      params.set(PRODUCT_GROUP_PARAM, topValue);
    } else {
      sidebarValues.forEach((value) => params.append(PRODUCT_GROUP_PARAM, value));
    }
  }

  function syncExclusiveBarToSidebar(input) {
    if (!isProductGroupInput(input)) return;

    document.querySelectorAll('.FilterBar[data-filter-bar-mode="multi"]').forEach((multiBar) => {
      multiBar.querySelectorAll(`[data-filter-input][name="${PRODUCT_GROUP_PARAM}"]`).forEach((other) => {
        other.checked = false;
        other.closest('.csb__check-item')?.classList.remove('is-active');
      });
    });
  }

  function syncSidebarProductGroupToTopbar() {
    document
      .querySelectorAll(`.FilterBar[data-filter-bar-mode="exclusive"] [data-filter-input][name="${PRODUCT_GROUP_PARAM}"]`)
      .forEach((radio) => {
        radio.checked = false;
        radio.closest('.csb__check-item')?.classList.remove('is-active');
      });
  }

  function updateExclusiveBarActiveState(filterBar, input) {
    filterBar.querySelectorAll('.csb__check-item.is-active').forEach((el) => {
      el.classList.remove('is-active');
    });
    input.closest('.csb__check-item')?.classList.add('is-active');
  }

  /* ── Build URL from all checked inputs (topbar + sidebar FilterBars) ── */
  // function buildFilterURL(page) {
  //   const params = new URLSearchParams();
  //   const filterBars = getFilterBars();

  //   filterBars.forEach((filterBar) => {
  //     if (!isExclusiveFilterBar(filterBar)) return;

  //     filterBar.querySelectorAll('[data-filter-input]:checked').forEach((input) => {
  //       if (isProductGroupInput(input)) return;
  //       params.set(input.name, input.value);
  //     });
  //   });

  //   filterBars.forEach((filterBar) => {
  //     if (isExclusiveFilterBar(filterBar)) return;

  //     filterBar.querySelectorAll('[data-filter-input]:checked').forEach((input) => {
  //       if (isProductGroupInput(input)) return;
  //       params.append(input.name, input.value);
  //     });

  //     const priceMin = filterBar.querySelector('input[name*="filter.v.price.gte"]');
  //     const priceMax = filterBar.querySelector('input[name*="filter.v.price.lte"]');

  //     if (priceMin && priceMin.value) {
  //       params.set(priceMin.name, priceMin.value);
  //     }

  //     if (priceMax && priceMax.value) {
  //       params.set(priceMax.name, priceMax.value);
  //     }
  //   });

  //   appendProductGroupParams(params, filterBars);

  //   const currentParams = new URLSearchParams(window.location.search);
  //   const sortBy = currentParams.get('sort_by');

  //   if (sortBy) {
  //     params.set('sort_by', sortBy);
  //   }

  //   if (page && page > 1) {
  //     params.set('page', page);
  //   }

  //   const qs = params.toString();
  //   const firstBar = filterBars[0];
  //   const collectionUrl = firstBar
  //     ? firstBar.dataset.collectionUrl || window.location.pathname
  //     : window.location.pathname;

  //   return qs ? `${collectionUrl}?${qs}` : collectionUrl;
  // }
  function buildFilterURL(page) {

    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);
    const filterBars = getFilterBars();

    /* Remove old filter params only */
    Array.from(params.keys()).forEach((key) => {
      if (
        key.startsWith('filter.') ||
        key === 'page'
      ) {
        params.delete(key);
      }
    });

    /* ---------- Exclusive filter bar ---------- */
    filterBars.forEach((filterBar) => {

      if (!isExclusiveFilterBar(filterBar)) return;

      filterBar.querySelectorAll('[data-filter-input]:checked').forEach((input) => {

        if (isProductGroupInput(input)) return;

        params.set(input.name, input.value);

      });

    });

    /* ---------- Sidebar ---------- */
    filterBars.forEach((filterBar) => {

      if (isExclusiveFilterBar(filterBar)) return;

      filterBar.querySelectorAll('[data-filter-input]:checked').forEach((input) => {

        if (isProductGroupInput(input)) return;

        params.append(input.name, input.value);

      });

      const priceMin = filterBar.querySelector(
        'input[name*="filter.v.price.gte"]'
      );

      const priceMax = filterBar.querySelector(
        'input[name*="filter.v.price.lte"]'
      );

      if (priceMin && priceMin.value) {
        params.set(priceMin.name, priceMin.value);
      }

      if (priceMax && priceMax.value) {
        params.set(priceMax.name, priceMax.value);
      }

    });

    /* ---------- Product Group ---------- */
    appendProductGroupParams(params, filterBars);

    /* ---------- Page ---------- */
    if (page > 1) {
      params.set('page', page);
    } else {
      params.delete('page');
    }

    /* ---------- Base URL ---------- */
    const firstBar = filterBars[0];

    const baseUrl = firstBar
      ? firstBar.dataset.collectionUrl || currentUrl.pathname
      : currentUrl.pathname;

    return `${baseUrl}?${params.toString()}`;

  }

  /* ── Fade in new product cards ───────────────────────── */
  function fadeInCards(cards) {
    cards.forEach((card, i) => {
      card.style.opacity = '0';
      card.style.transition = `opacity 0.4s ease ${i * 0.05}s`;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          card.style.opacity = '1';
        });
      });
    });
  }

  /* ── AJAX fetch → replace grid (used when filter changes) ── */
  function clearFilterTopbar(clearBtn) {
    const topbarButton = document.querySelector('[data-csb-clear-button-topbar]');

    if (!topbarButton || !clearBtn) return;

    topbarButton.onclick = (e) => {
      e.preventDefault();
      clearBtn.click();
    };
  }

  function clearFilters() {

    const trigger = document.querySelector('[data-csb-clear-button]');
    

    if (!trigger) return;

    trigger.addEventListener('click', function (e) {

      e.preventDefault();

      // Reset all filter inputs (top bar + sidebar)
      getFilterBars().forEach(function (filterBar) {
        filterBar.querySelectorAll('[data-filter-input]').forEach(function (input) {
          if (input.type === 'checkbox' || input.type === 'radio') {
            input.checked = false;
          }

          if (input.classList.contains('price-range__input')) {
            input.value = '';
          }
        });
      });

      const filterBar = getFilterBars()[0];

      // Update results button instantly
      const resultsButtonText = document.querySelector('.csb__results-button-text');

      if (resultsButtonText) {
        resultsButtonText.textContent = 'Show results';
      }

      const resultsButton = document.querySelector('[data-csb-results-button]');

      if (resultsButton) {
        resultsButton.classList.remove('is-active');
      }

      // Reset pagination state
      currentPage = 1;

      // Prevent instant infinite scroll trigger
      suppressNextIntersection = true;

      // Clean URL
      const cleanUrl = filterBar.dataset.collectionUrl || window.location.pathname;

      history.pushState({}, '', cleanUrl);

      // Reload collection from Shopify
      fetchFilteredProducts(cleanUrl);

      
    });

    clearFilterTopbar(trigger);
  }

  function applyFilters(filterBar) {
    currentPage = 1;

    // Prevent instant page 2 load
    suppressNextIntersection = true;

    const url = buildFilterURL(1);
    history.pushState({}, '', url);
    fetchFilteredProducts(url);
  }

  function initProductSwipers(scope = document) {
    if (typeof Swiper === 'undefined') return;

    scope.querySelectorAll('.l-product-card-swiper').forEach((slider) => {
      slider.swiper?.destroy(true, true);

      new Swiper(slider, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: true,
        speed: 600,
        autoplay: false,
        pagination: {
          el: slider.querySelector('.swiper-pagination'),
          clickable: true,
        },
        breakpoints: {
          768: {
            allowTouchMove: true,
          },
        },
      });
    });
  }

  function openPreview(card, swatch) {
    const media = card.querySelector('[data-product-media]');
    const badge = card.querySelector('[data-product-badge]');
    const title = card.querySelector('[data-product-title]');
    const conditions = card.querySelector('[data-product-conditions]');
    const prices = card.querySelector('[data-product-price]');
    const btn_more = card.querySelector('.l-product-group__more');

    if (media) {
      media.innerHTML = swatch.dataset.media;
    }

    if (badge) {
      badge.innerHTML = swatch.dataset.badge;
    }

    if (title) {
      title.href = swatch.dataset.url;
      title.innerHTML = swatch.dataset.title;
    }

    if (conditions) {
      conditions.innerHTML = swatch.dataset.conditions;
    }

    if (prices) {
      prices.innerHTML = swatch.dataset.price;
    }

    if (btn_more) {
      btn_more.href = swatch.dataset.url;
    }

    card
      .querySelectorAll('.l-product-group__item')
      .forEach(item => item.classList.remove('is-selected'));

    swatch.classList.add('is-selected');

    // Recreate Swiper because media HTML has changed
    initProductSwipers(card);
  }

  function fetchFilteredProducts(url) {
    const grid      = getGrid();
    const activeTags = document.getElementById('ActiveTags');

    if (grid) {
      grid.style.opacity = '0.5';
      grid.style.pointerEvents = 'none';
    }

    const sectionId = getSectionId();
    const fetchUrl  = sectionId ? `${url}${url.includes('?') ? '&' : '?'}section_id=${sectionId}` : url;

    fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(res => {
        if (!res.ok) throw new Error('Filter fetch failed: ' + res.status);
        return res.text();
      })
      .then(html => {
        const parser = new DOMParser();
        const doc    = parser.parseFromString(html, 'text/html');

        // Replace grid content
        const newGrid = doc.getElementById('ProductGrid');
        if (grid && newGrid) {
          grid.innerHTML = newGrid.innerHTML;
          grid.style.opacity = '';
          grid.style.pointerEvents = '';
          const cards = Array.from(grid.children);
          fadeInCards(cards);
          initProductSwipers(grid);
        }

        // Swap active tags
        const newTags = doc.getElementById('ActiveTags');
        if (activeTags) {
          activeTags.innerHTML = newTags ? newTags.innerHTML : '';
        } else if (newTags) {
          const layout = document.querySelector('.collection-layout');
          if (layout) layout.insertAdjacentElement('afterbegin', newTags);
        }

        // Update product count
        const newCount = doc.querySelector('.product-count');
        if (newCount) {
          const resultsButton = document.querySelector('[data-csb-results-button]');
          if(resultsButton) {
            const resultsButtonText = resultsButton.querySelector('.csb__results-button-text');

            const checkedCount = document.querySelectorAll(
              `[data-filter-input]:checked`
            ).length;

            resultsButtonText.textContent = checkedCount >= 1
            ? `Show (${newCount.textContent.replace(/\s+/g, ' ').trim()}) results`
            : 'Show results';
          }
        }

        // Save currently opened accordion sections BEFORE AJAX replaces HTML
        const openSections = Array.from(
          document.querySelectorAll('.csb__section.is-open [data-csb-toggle]')
        ).map(el => el.dataset.csbId);

        // Swap filter bar
        const newFilterBars = doc.querySelectorAll('.FilterBar');
        const curFilterBars = document.querySelectorAll('.FilterBar');
        curFilterBars.forEach((bar, index) => {
          if (newFilterBars[index]) {
            bar.innerHTML = newFilterBars[index].innerHTML;
          }
        });
        
        // Restore accordion open states AFTER HTML replacement
        openSections.forEach(id => {

          const trigger = document.querySelector(
            `[data-csb-id="${id}"]`
          );

          if (!trigger) return;

          const section = trigger.closest('.csb__section');

          section.classList.add('is-open');

          trigger.setAttribute('aria-expanded', 'true');

        });

        // Re-init after DOM replacement
        initializeFilterAccordion();
        initializeSortBy();

        initCollectionTabsScroll();

        if (typeof window.remeasureCollectionTopbar === 'function') {
          window.remeasureCollectionTopbar();
        } else {
          if (typeof window.updateCollectionTopbarSticky === 'function') {
            window.updateCollectionTopbarSticky();
          }
          if (typeof window.alignCollectionSubnavToHeader === 'function') {
            window.alignCollectionSubnavToHeader();
          }
        }

        // Update pagination data for infinite scroll
        const newPagination = doc.querySelector('.pagination[data-total-pages]');
        const curPagination = document.querySelector('.pagination[data-total-pages]');
        if (newPagination) {
          totalPages = parseInt(newPagination.dataset.totalPages) || 1;

          if (isBestSellingSort()) {
            totalPages = 1;
          }

          if (curPagination) {
            curPagination.dataset.totalPages = totalPages;
          }
        } else {
          totalPages = 1;
        }

        currentPage = 1;
        isLoading   = false;

        // Remove load more trigger and re-observe
        setupInfiniteScroll();
        clearFilterTopbar(document.querySelector('[data-csb-clear-button]'));
      })
      .catch(err => {
        console.error('[CollectionFilter]', err);
        if (grid) {
          grid.style.opacity = '';
          grid.style.pointerEvents = '';
        }
        isLoading = false;
    });

  }

  function isBestSellingSort() {
    const params = new URLSearchParams(window.location.search);
    return params.get('sort_by') === 'best-selling';
  }

  // Sortby by click
  function initializeSortBy() {

    const triggers = document.querySelectorAll('[data-sort]');

    if (!triggers.length) return;

    const currentParams = new URLSearchParams(window.location.search);

    const currentSort = currentParams.get('sort_by');

    triggers.forEach(function (trigger) {

      // Restore active state on reload
      if (currentSort && trigger.dataset.sort === currentSort) {

        trigger.classList.add('is-active');

        const dot = trigger.querySelector('.csb__radio-dot');

        if (dot) {
          dot.classList.add('is-checked');
        }

        // Add is-open to Sort section
        const section = trigger.closest('.csb__section');

        if (section) {
          section.classList.add('is-open');
        }

      }

      trigger.addEventListener('click', function (e) {

        e.preventDefault();

        const sortLink = e.currentTarget;

        const filterBar = getParentFilterBar(sortLink);

        if (!filterBar) return;

        const params = new URLSearchParams(window.location.search);

        params.set('sort_by', sortLink.dataset.sort);

        params.delete('page');

        const url = `${getCollectionUrl()}?${params.toString()}`;

        // Remove active states
        triggers.forEach(function (item) {

          item.classList.remove('is-active');

          const dot = item.querySelector('.csb__radio-dot');

          if (dot) {
            dot.classList.remove('is-checked');
          }

        });

        // Add active state
        sortLink.classList.add('is-active');

        const activeDot = sortLink.querySelector('.csb__radio-dot');

        if (activeDot) {
          activeDot.classList.add('is-checked');
        }

        // Add is-open to sort section
        const section = sortLink.closest('.csb__section');

        if (section) {
          section.classList.add('is-open');
        }

        // Update results count button
        // updateResultsButton(filterBar);

        // Update URL
        history.pushState({}, '', url);

        // AJAX reload
        fetchFilteredProducts(url);

      });

    });

  }
  
  // Sortby by select dropdown
  // function initializeSortBy() {
  //   const select = document.querySelector('.csb__sort-select');

  //   if (!select) return;

  //   // Restore current sort
  //   const params = new URLSearchParams(window.location.search);
  //   const currentSort = params.get('sort_by');

  //   if (currentSort) {
  //     select.value = currentSort;
  //   }

  //   select.addEventListener('change', function () {
  //     const filterBar = getParentFilterBar(select);

  //     if (!filterBar) return;

  //     const params = new URLSearchParams(window.location.search);

  //     params.set('sort_by', select.value);
  //     params.delete('page');

  //     const url = `${getCollectionUrl()}?${params.toString()}`;

  //     // Keep Sort section open
  //     const section = select.closest('.csb__section');
  //     if (section) {
  //       section.classList.add('is-open');
  //     }

  //     // Update URL
  //     history.pushState({}, '', url);

  //     // AJAX reload
  //     fetchFilteredProducts(url);
  //   });
  // }

  /* ── Infinite scroll — append next page ─────────────── */
  function loadNextPage() {
    if (isLoading || currentPage >= totalPages) return;

    isLoading = true;
    currentPage++;

    const url = buildFilterURL(currentPage);
    const sectionId = getSectionId();
    const fetchUrl  = sectionId ? `${url}${url.includes('?') ? '&' : '?'}section_id=${sectionId}` : url;

    // Show loader
    showLoader();

    fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(res => {
        if (!res.ok) throw new Error('Page fetch failed: ' + res.status);
        return res.text();
      })
      .then(html => {
        const parser  = new DOMParser();
        const doc     = parser.parseFromString(html, 'text/html');
        const newGrid = doc.getElementById('ProductGrid');
        const grid    = getGrid();

        if (grid && newGrid) {
          const newCards = Array.from(newGrid.children);
          newCards.forEach(card => grid.appendChild(card));
          fadeInCards(newCards);
          initProductSwipers(grid);
        }

        hideLoader();
        isLoading = false;

        // Update history URL to reflect current page
        history.replaceState({}, '', buildFilterURL(currentPage));
      })
      .catch(err => {
        console.error('[CollectionFilter] infinite scroll error:', err);
        hideLoader();
        isLoading = false;
        currentPage--;
      });
  }

  /* ── Loader ──────────────────────────────────────────── */
  function showLoader() {
    let loader = document.getElementById('scroll-loader');
    if (!loader) {
      loader = document.createElement('div');
      loader.id = 'scroll-loader';
      loader.innerHTML = '<span></span><span></span><span></span>';
      loader.style.cssText = 'display:flex;justify-content:center;align-items:center;gap:6px;padding:32px;width:100%;';
      loader.querySelectorAll('span').forEach((s, i) => {
        s.style.cssText = `width:8px;height:8px;border-radius:50%;background:#C0392B;opacity:0.3;animation:scroll-bounce 0.8s ease-in-out ${i * 0.15}s infinite alternate;`;
      });

      const style = document.createElement('style');
      style.textContent = '@keyframes scroll-bounce{0%{opacity:0.3;transform:translateY(0)}100%{opacity:1;transform:translateY(-6px)}}';
      document.head.appendChild(style);

      const grid = getGrid();
      if (grid) grid.insertAdjacentElement('afterend', loader);
    }
    loader.style.display = 'flex';
  }

  function hideLoader() {
    const loader = document.getElementById('scroll-loader');
    if (loader) loader.style.display = 'none';
  }

  function initializeFilterAccordion() {

    document.querySelectorAll('[data-csb-toggle]').forEach(function (trigger) {

      trigger.addEventListener('click', function (e) {

        e.preventDefault();

        const toggle = e.currentTarget;

        const section = toggle.closest('.csb__section');
        if (!section) return;

        const container = toggle.closest('.csb');
        if (!container) return;

        // Toggle open/close
        section.classList.toggle('is-open');

        toggle.setAttribute(
          'aria-expanded',
          section.classList.contains('is-open') ? 'true' : 'false'
        );
      });

    });

  }

  /* ── Infinite scroll observer ────────────────────────── */
  let scrollObserver = null;
  let sentinel      = null;

  function setupInfiniteScroll() {
    totalPages = getTotalPages();
    // Cap Best Selling to 24 products (1 pages × 24 products)
    if (isBestSellingSort()) {
      totalPages = 1;
    }

    // Remove old sentinel
    if (sentinel) sentinel.remove();

    if (scrollObserver) scrollObserver.disconnect();

    if (totalPages <= 1) return;

    // Create sentinel element at bottom of grid
    sentinel = document.createElement('div');
    sentinel.id = 'scroll-sentinel';
    sentinel.style.cssText = 'height:1px;width:100%;';

    const grid = getGrid();
    if (grid) grid.insertAdjacentElement('afterend', sentinel);

    scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {

        // Ignore first automatic intersection after filtering
        if (suppressNextIntersection) {
          suppressNextIntersection = false;
          return;
        }

        if (entry.isIntersecting && !isLoading && currentPage < totalPages) {
          loadNextPage();
        }
      });
    }, { rootMargin: '200px' });

    scrollObserver.observe(sentinel);
  }

  /* ── Collection tabs horizontal scroll ─────────────── */

  function getCollectionTabItems(track) {
    return Array.from(track.children).filter((el) => el.matches('a, label'));
  }

  function updateCollectionTabsScrollState(wrapper) {
    const viewport = wrapper.querySelector('.l-collection-tabs__viewport');
    if (!viewport) return;

    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const scrollLeft = viewport.scrollLeft;
    const hasOverflow = maxScroll > 1;
    const canScrollLeft = scrollLeft > 1;
    const canScrollRight = scrollLeft < maxScroll - 1;

    wrapper.classList.toggle('has-overflow', hasOverflow);
    wrapper.classList.toggle('can-scroll-left', canScrollLeft);
    wrapper.classList.toggle('can-scroll-right', canScrollRight);

    // const prevBtn = wrapper.querySelector('[data-collection-tabs-scroll="prev"]');
    // const nextBtn = wrapper.querySelector('[data-collection-tabs-scroll="next"]');

    // if (prevBtn) {
    //   prevBtn.disabled = !canScrollLeft;
    //   prevBtn.hidden = !canScrollLeft;
    // }
    // if (nextBtn) {
    //   nextBtn.disabled = !canScrollRight;
    //   nextBtn.hidden = !canScrollRight;
    // }
  }

  function getLeftmostVisibleTabIndex(items, scrollLeft) {
    let index = 0;

    for (let i = 0; i < items.length; i++) {
      if (items[i].offsetLeft >= scrollLeft - 1) {
        index = i;
        break;
      }
    }

    return index;
  }

  function scrollCollectionTabsOne(wrapper, direction) {
    const viewport = wrapper.querySelector('.l-collection-tabs__viewport');
    const track = wrapper.querySelector('.l-collection-tabs--groups');
    if (!viewport || !track) return;

    const items = getCollectionTabItems(track);
    if (!items.length) return;

    const viewLeft = viewport.scrollLeft;
    const leftmostIndex = getLeftmostVisibleTabIndex(items, viewLeft);
    const targetIndex = direction === 'next'
      ? Math.min(items.length - 1, leftmostIndex + 1)
      : Math.max(0, leftmostIndex - 1);

    viewport.scrollTo({ left: items[targetIndex].offsetLeft, behavior: 'smooth' });
  }

  function bindCollectionTabsScroll(wrapper) {
    const viewport = wrapper.querySelector('.l-collection-tabs__viewport');
    if (!viewport || viewport.dataset.scrollBound === 'true') return;

    viewport.dataset.scrollBound = 'true';
    viewport.addEventListener('scroll', () => {
      updateCollectionTabsScrollState(wrapper);
    }, { passive: true });
  }

  function initCollectionTabsScroll() {
    document.querySelectorAll('.l-collection-tabs-wrapper').forEach((wrapper) => {
      bindCollectionTabsScroll(wrapper);
      updateCollectionTabsScrollState(wrapper);
    });
  }

  window.initCollectionTabsScroll = initCollectionTabsScroll;

  // 4. Filter change → AJAX replace
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-filter-input]');
    if (!input) return;

    const filterBar = getParentFilterBar(input);
    if (!filterBar) return;

    if (input.classList.contains('price-range__input')) return;

    if (isExclusiveFilterBar(filterBar)) {
      if (isProductGroupInput(input)) {
        syncExclusiveBarToSidebar(input);
      }
      updateExclusiveBarActiveState(filterBar, input);
    } else if (isProductGroupInput(input)) {
      syncSidebarProductGroupToTopbar();
    }

    applyFilters(filterBar);
    // updateResultsButton(filterBar);
  });

  document.addEventListener('click', (e) => {
    const scrollBtn = e.target.closest('[data-collection-tabs-scroll]');
    if (scrollBtn) {
      const wrapper = scrollBtn.closest('.l-collection-tabs-wrapper');
      if (!wrapper || scrollBtn.disabled) return;
      scrollCollectionTabsOne(wrapper, scrollBtn.dataset.collectionTabsScroll);
      return;
    }


    // Swatch preview
    const swatch = e.target.closest(
      '.l-product-group__colorswatch--container .l-product-group__item'
    );

    if (swatch) {
      e.preventDefault();

      const card = swatch.closest('l-product-card');
      if (!card) return;

      openPreview(card, swatch);
      return;
    }


    const filterTag = e.target.closest('[data-filter-remove]');
    if (filterTag) {
      e.preventDefault();
      currentPage = 1;
      suppressNextIntersection = true;
      const url = filterTag.getAttribute('href');
      if (!url) return;
      history.pushState({}, '', url);
      fetchFilteredProducts(url);
      return;
    }

    const sortLink = e.target.closest('[data-sort]');

    if (!sortLink) return;

    e.preventDefault();

    const params = new URLSearchParams(window.location.search);

    params.set('sort_by', sortLink.dataset.sort);

    params.delete('page');

    const url = `${getCollectionUrl()}?${params.toString()}`;

    // Update active state
    document.querySelectorAll('[data-sort]').forEach(link => {

      link.classList.remove('is-active');

      const dot = link.querySelector('.csb__radio-dot');

      if (dot) {
        dot.classList.remove('is-checked');
      }

    });

    sortLink.classList.add('is-active');

    const activeDot = sortLink.querySelector('.csb__radio-dot');

    if (activeDot) {
      activeDot.classList.add('is-checked');
    }

    // Update URL
    history.pushState({}, '', url);

    // AJAX reload
    fetchFilteredProducts(url);

  });

  /* ── Init ────────────────────────────────────────────── */
  setupInfiniteScroll();
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initializeFilterAccordion();
      initializeSortBy();
      clearFilters();
      initCollectionTabsScroll();
      initProductSwipers();
    });
  } else {
    initializeFilterAccordion();
    initializeSortBy();
    clearFilters();
    initCollectionTabsScroll();
    initProductSwipers();
  }

  window.addEventListener('resize', initCollectionTabsScroll);

  

})();
